import { useState } from 'react';
import nhost from '../nhost';

export default function Auth() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPassword2, setShowNewPassword2] = useState(false);

  const MIN_PASSWORD_LENGTH = 9;

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await nhost.auth.signIn({ email, password });
    if (error) setError('Fel e-post eller lösenord.');
    setLoading(false);
  }

  async function handleRegister(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!displayName.trim()) { setError('Ange ditt namn.'); setLoading(false); return; }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Lösenordet måste vara minst ${MIN_PASSWORD_LENGTH} tecken.`);
      setLoading(false);
      return;
    }
    const { error } = await nhost.auth.signUp({
      email, password,
      options: { displayName: displayName.trim() }
    });
    if (error) {
      if (error.message.includes('already')) setError('E-postadressen används redan.');
      else if (error.error === 'password-too-short' || error.message.toLowerCase().includes('password')) {
        setError(`Lösenordet måste vara minst ${MIN_PASSWORD_LENGTH} tecken.`);
      } else {
        setError('Något gick fel. Försök igen.');
      }
    } else {
      setRegistered(true);
    }
    setLoading(false);
  }

  async function handleRequestCode(e) {
    e.preventDefault();
    if (!email.trim()) { setError('Ange din e-postadress.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Något gick fel. Försök igen.');
      } else {
        setResetToken(data.token);
        setMode('forgot-code');
      }
    } catch {
      setError('Något gick fel. Försök igen.');
    }
    setLoading(false);
  }

  async function handleConfirmCode(e) {
    e.preventDefault();
    if (!resetCode.trim()) { setError('Ange koden från mailet.'); return; }
    if (!newPassword || newPassword.length < MIN_PASSWORD_LENGTH) { setError(`Lösenordet måste vara minst ${MIN_PASSWORD_LENGTH} tecken.`); return; }
    if (newPassword !== newPassword2) { setError('Lösenorden matchar inte.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'confirm', token: resetToken, code: resetCode.trim(), newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Något gick fel. Försök igen.');
      } else {
        setResetDone(true);
      }
    } catch {
      setError('Något gick fel. Försök igen.');
    }
    setLoading(false);
  }

  function backToLogin() {
    setMode('login');
    setResetDone(false);
    setResetToken(null);
    setResetCode('');
    setNewPassword('');
    setNewPassword2('');
    setError('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !loading) {
      if (mode === 'login') handleLogin(e);
      else if (mode === 'register') handleRegister(e);
      else if (mode === 'forgot') handleRequestCode(e);
      else if (mode === 'forgot-code') handleConfirmCode(e);
    }
  }

  if (resetDone) {
    return (
      <div className="auth-screen">
        <div className="auth-logo">Vinligan</div>
        <div style={{ fontSize: 48, margin: '20px 0' }}>✅</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, marginBottom: 12 }}>Lösenord ändrat!</h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6, maxWidth: 280 }}>
          Ditt lösenord har uppdaterats. Logga in med ditt nya lösenord.
        </p>
        <button className="btn-primary" style={{ marginTop: 24, maxWidth: 280 }} onClick={backToLogin}>
          Logga in
        </button>
      </div>
    );
  }

  if (mode === 'forgot-code') {
    return (
      <div className="auth-screen">
        <div className="auth-logo">Vinligan</div>
        <div className="auth-sub">Ange kod från mailet</div>
        <div className="auth-form">
          {error && <div className="auth-error">{error}</div>}
          <div className="field">
            <label>Kod</label>
            <input
              value={resetCode}
              onChange={e => setResetCode(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="XXXX-XXXX"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
            />
          </div>
          <div className="field">
            <label>Nytt lösenord</label>
            <div className="password-input-wrap">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Minst ${MIN_PASSWORD_LENGTH} tecken`}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(v => !v)}
                tabIndex={-1}
                aria-label={showNewPassword ? 'Dölj lösenord' : 'Visa lösenord'}
              >
                {showNewPassword ? '🙈' : '👁'}
              </button>
            </div>
            <div className="field-hint">Minst {MIN_PASSWORD_LENGTH} tecken.</div>
          </div>
          <div className="field">
            <label>Bekräfta lösenord</label>
            <div className="password-input-wrap">
              <input
                type={showNewPassword2 ? 'text' : 'password'}
                value={newPassword2}
                onChange={e => setNewPassword2(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Upprepa lösenordet"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword2(v => !v)}
                tabIndex={-1}
                aria-label={showNewPassword2 ? 'Dölj lösenord' : 'Visa lösenord'}
              >
                {showNewPassword2 ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          <button className="btn-primary" onClick={handleConfirmCode} disabled={loading}>
            {loading ? 'Sparar...' : 'Byt lösenord'}
          </button>
          <div className="auth-toggle">
            <span>Fick du ingen kod? <button onClick={() => { setMode('forgot'); setError(''); }}>Skicka igen</button></span>
          </div>
        </div>
      </div>
    );
  }

  if (registered) {
    return (
      <div className="auth-screen">
        <div className="auth-logo">Vinligan</div>
        <div style={{ fontSize: 48, margin: '20px 0' }}>📬</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, marginBottom: 12 }}>Tack för din ansökan!</h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6, maxWidth: 280 }}>
          Din ansökan väntar på godkännande av admin. Du får tillgång så snart den är godkänd.
        </p>
        <button className="btn-secondary" style={{ marginTop: 24, maxWidth: 200 }} onClick={() => { setRegistered(false); setMode('login'); }}>
          Tillbaka till inloggning
        </button>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <div className="auth-screen">
        <div className="auth-logo">Vinligan</div>
        <div className="auth-sub">Återställ lösenord</div>
        <div className="auth-form">
          {error && <div className="auth-error">{error}</div>}
          <div className="field">
            <label>Din e-postadress</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={handleKeyDown} placeholder="din@email.com" autoFocus />
          </div>
          <button className="btn-primary" onClick={handleRequestCode} disabled={loading}>
            {loading ? 'Skickar...' : 'Skicka kod'}
          </button>
          <div className="auth-toggle">
            <span>Kom du ihåg lösenordet? <button onClick={() => { setMode('login'); setError(''); }}>Logga in</button></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-logo">Vinligan</div>
      <div className="auth-sub">Er vinprovningsgrupp</div>
      <div className="auth-form">
        {error && <div className="auth-error">{error}</div>}
        {mode === 'register' && (
          <div className="field">
            <label>Ditt namn</label>
            <input value={displayName} onChange={e => setDisplayName(e.target.value)} onKeyDown={handleKeyDown} placeholder="Förnamn Efternamn" />
          </div>
        )}
        <div className="field">
          <label>E-post</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={handleKeyDown} placeholder="din@email.com" />
        </div>
        <div className="field">
          <label>Lösenord</label>
          <div className="password-input-wrap">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(v => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
            >
              {showPassword ? '🙈' : '👁'}
            </button>
          </div>
          {mode === 'register' && (
            <div className="field-hint">Minst {MIN_PASSWORD_LENGTH} tecken.</div>
          )}
        </div>
        <button className="btn-primary" onClick={mode === 'login' ? handleLogin : handleRegister} disabled={loading}>
          {loading ? 'Laddar...' : mode === 'login' ? 'Logga in' : 'Skapa konto'}
        </button>
        {mode === 'login' && (
          <div className="auth-toggle">
            <button onClick={() => { setMode('forgot'); setError(''); }} style={{ color: 'var(--muted)', fontSize: 12 }}>
              Glömt lösenordet?
            </button>
          </div>
        )}
        <div className="auth-toggle">
          {mode === 'login' ? (
            <span>Ny i Vinligan? <button onClick={() => setMode('register')}>Skapa konto</button></span>
          ) : (
            <span>Har du redan konto? <button onClick={() => setMode('login')}>Logga in</button></span>
          )}
        </div>
      </div>
    </div>
  );
}
